<template>
    <v-dialog v-model="internalValue" max-width="650" persistent scrollable>
        <v-card class="excel-dialog rounded-xl overflow-hidden" elevation="12">

            <!-- Header con gradiente -->
            <div class="dialog-header">
                <div class="d-flex align-center">
                    <v-avatar color="rgba(255,255,255,0.15)" size="44" class="mr-3">
                        <v-icon color="white" size="24">mdi-microsoft-excel</v-icon>
                    </v-avatar>
                    <div>
                        <div class="dialog-title">Seleccionar Hojas para Exportar</div>
                        <div class="dialog-subtitle">Elige qué hojas incluir en el archivo Excel</div>
                    </div>
                </div>
                <v-btn icon dark @click="cancel" class="ml-auto close-btn">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </div>

            <v-card-text class="px-5 pt-5 pb-0">

                <!-- Toggle Resumen General -->
                <div
                    class="resumen-card mb-4"
                    :class="{ 'resumen-card--active': includeResumen }"
                    @click="includeResumen = !includeResumen"
                >
                    <div class="d-flex align-center">
                        <v-avatar
                            :color="includeResumen ? 'primary' : 'grey lighten-2'"
                            size="40"
                            class="mr-3 resumen-avatar"
                        >
                            <v-icon :color="includeResumen ? 'white' : 'grey darken-1'" size="20">
                                mdi-table-large
                            </v-icon>
                        </v-avatar>
                        <div>
                            <div class="resumen-label">Resumen General</div>
                            <div class="resumen-sublabel">Hoja de consolidado (SHIPMENTS)</div>
                        </div>
                    </div>
                    <v-switch
                        v-model="includeResumen"
                        inset
                        dense
                        hide-details
                        color="primary"
                        @click.stop
                        class="mt-0 pt-0"
                    />
                </div>

                <v-divider class="mb-4" />

                <!-- Sección Semanas -->
                <div class="d-flex align-center justify-space-between mb-3 flex-wrap" style="gap: 8px;">
                    <div class="weeks-section-title">
                        <v-icon small color="primary" class="mr-1">mdi-calendar-week</v-icon>
                        Semanas de Departure WK
                    </div>
                    <div class="d-flex flex-wrap" style="gap: 6px;">
                        <v-btn
                            v-if="availableWeeks && availableWeeks.length > 0"
                            x-small
                            outlined
                            color="success"
                            class="rounded-pill text-none px-3 font-weight-bold"
                            @click="selectAvailableOnly"
                        >
                            <v-icon x-small left>mdi-calendar-check</v-icon>Solo con Datos ({{ availableWeeks.length }})
                        </v-btn>
                        <v-btn x-small outlined color="primary" class="rounded-pill text-none px-3" @click="selectAll">
                            <v-icon x-small left>mdi-check-all</v-icon>Todas
                        </v-btn>
                        <v-btn x-small outlined color="grey darken-1" class="rounded-pill text-none px-3" @click="selectNone">
                            <v-icon x-small left>mdi-close</v-icon>Ninguna
                        </v-btn>
                    </div>
                </div>

                <!-- Grid de semanas -->
                <div class="weeks-grid mb-3">
                    <v-tooltip
                        v-for="wk in 52"
                        :key="wk"
                        top
                        :disabled="hasData(wk)"
                    >
                        <template v-slot:activator="{ on, attrs }">
                            <div
                                v-bind="attrs"
                                v-on="on"
                                class="week-chip"
                                :class="{
                                    'week-chip--selected': selectedWeeks.includes(wk),
                                    'week-chip--current': wk === currentWeek,
                                    'week-chip--has-data': hasData(wk) && wk !== currentWeek,
                                    'week-chip--no-data': !hasData(wk)
                                }"
                                @click="toggleWeek(wk)"
                            >
                                <v-icon v-if="wk === currentWeek" x-small class="week-star">mdi-star</v-icon>
                                <span v-else-if="hasData(wk)" class="data-dot" title="Tiene datos"></span>
                                {{ wk }}
                            </div>
                        </template>
                        <span>Sin registros para este cliente en la semana {{ wk }}</span>
                    </v-tooltip>
                </div>

                <!-- Alerta si hay semanas seleccionadas sin datos -->
                <v-alert
                    v-if="selectedEmptyWeeks.length > 0"
                    dense
                    text
                    type="warning"
                    icon="mdi-alert-circle-outline"
                    class="rounded-lg mb-4 text-caption"
                >
                    Las siguientes semanas seleccionadas no contienen registros y no generarán hoja en el Excel:
                    <strong>Departure WK {{ selectedEmptyWeeks.join(', WK ') }}</strong>.
                </v-alert>

            </v-card-text>

            <!-- Footer fijo -->
            <v-card-actions class="dialog-footer px-5 py-4">
                <div class="selected-counter">
                    <v-icon small color="primary" class="mr-1">mdi-layers</v-icon>
                    <span v-if="totalSelected === 0" class="counter-text grey--text">Ninguna hoja seleccionada</span>
                    <span v-else class="counter-text">
                        <strong class="primary--text">{{ totalSelected }}</strong>
                        {{ totalSelected === 1 ? ' hoja seleccionada' : ' hojas seleccionadas' }}
                    </span>
                </div>
                <v-spacer />
                <v-btn
                    text
                    class="rounded-lg text-none px-4 mr-2"
                    @click="cancel"
                >
                    Cancelar
                </v-btn>
                <v-btn
                    color="primary"
                    class="rounded-lg text-none px-5 font-weight-bold elevation-2"
                    :disabled="totalSelected === 0"
                    @click="confirm"
                >
                    <v-icon left small>mdi-download</v-icon>
                    Descargar Excel
                </v-btn>
            </v-card-actions>

        </v-card>
    </v-dialog>
</template>

<script>
export default {
    name: 'ExcelSheetsDialog',
    props: {
        value: {
            type: Boolean,
            default: false
        },
        currentWeek: {
            type: Number,
            default: 1
        },
        availableWeeks: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            includeResumen: true,
            selectedWeeks: []
        };
    },
    computed: {
        internalValue: {
            get() { return this.value; },
            set(val) { this.$emit('input', val); }
        },
        totalSelected() {
            return (this.includeResumen ? 1 : 0) + this.selectedWeeks.length;
        },
        selectedEmptyWeeks() {
            if (!this.availableWeeks || this.availableWeeks.length === 0) return [];
            return this.selectedWeeks.filter(wk => !this.availableWeeks.includes(wk)).sort((a, b) => a - b);
        }
    },
    watch: {
        value(newVal) {
            if (newVal) {
                this.includeResumen = true;
                this.selectedWeeks = [];
            }
        }
    },
    methods: {
        hasData(wk) {
            if (!this.availableWeeks || this.availableWeeks.length === 0) return true;
            return this.availableWeeks.includes(wk);
        },
        toggleWeek(wk) {
            const idx = this.selectedWeeks.indexOf(wk);
            if (idx >= 0) {
                this.selectedWeeks.splice(idx, 1);
            } else {
                this.selectedWeeks.push(wk);
            }
        },
        selectAll() {
            this.selectedWeeks = Array.from({ length: 52 }, (_, i) => i + 1);
        },
        selectNone() {
            this.selectedWeeks = [];
        },
        selectCurrent() {
            this.selectedWeeks = [this.currentWeek];
        },
        selectAvailableOnly() {
            if (this.availableWeeks && this.availableWeeks.length > 0) {
                this.selectedWeeks = [...this.availableWeeks];
            }
        },
        confirm() {
            const sheets = [];
            if (this.includeResumen) sheets.push(-1);
            const sorted = [...this.selectedWeeks].sort((a, b) => a - b);
            sheets.push(...sorted);
            this.$emit('confirm', sheets);
        },
        cancel() {
            this.$emit('input', false);
        }
    }
};
</script>

<style scoped>
/* Header */
.dialog-header {
    background: linear-gradient(135deg, #1a1f36 0%, #2d3561 60%, #3a4a8a 100%);
    padding: 20px 20px 20px 24px;
    display: flex;
    align-items: center;
}
.dialog-title {
    color: #ffffff;
    font-size: 16px;
    font-weight: 700;
    letter-spacing: 0.2px;
    line-height: 1.3;
}
.dialog-subtitle {
    color: rgba(255,255,255,0.65);
    font-size: 12px;
    margin-top: 2px;
}
.close-btn {
    opacity: 0.7;
    transition: opacity 0.2s;
}
.close-btn:hover { opacity: 1; }

/* Resumen Card */
.resumen-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border: 2px solid #e0e0e0;
    border-radius: 14px;
    padding: 12px 16px;
    cursor: pointer;
    transition: all 0.2s ease;
    background: #fafafa;
}
.resumen-card:hover {
    border-color: #90a4d4;
    background: #f0f4ff;
}
.resumen-card--active {
    border-color: #1565C0;
    background: #e8f0fe;
}
.resumen-avatar {
    transition: background-color 0.2s;
}
.resumen-label {
    font-weight: 600;
    font-size: 14px;
    color: #212121;
}
.resumen-sublabel {
    font-size: 11px;
    color: #757575;
    margin-top: 1px;
}

/* Semanas */
.weeks-section-title {
    font-size: 13px;
    font-weight: 600;
    color: #424242;
}
.weeks-grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 6px;
}
.week-chip {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 36px;
    border-radius: 8px;
    border: 1.5px solid #e0e0e0;
    background: #f5f5f5;
    font-size: 12px;
    font-weight: 500;
    color: #424242;
    cursor: pointer;
    user-select: none;
    transition: all 0.15s ease;
    flex-direction: column;
    line-height: 1;
}
.week-chip:hover {
    border-color: #90a4d4;
    background: #e8f0fe;
    color: #1565C0;
    transform: scale(1.06);
}
.week-chip--has-data:not(.week-chip--selected) {
    border-color: #90CAF9;
    color: #1565C0;
    background: #E3F2FD;
    font-weight: 600;
}
.week-chip--no-data:not(.week-chip--selected) {
    opacity: 0.45;
    border-style: dashed;
    background: #FAFAFA;
}
.week-chip--selected {
    background: #1565C0;
    border-color: #1565C0;
    color: #ffffff;
    font-weight: 700;
    box-shadow: 0 2px 6px rgba(21, 101, 192, 0.35);
}
.week-chip--selected:hover {
    background: #1976D2;
    border-color: #1976D2;
    color: #ffffff;
}
.week-chip--current:not(.week-chip--selected) {
    border-color: #43A047;
    color: #2E7D32;
    background: #E8F5E9;
    font-weight: 700;
}
.week-chip--current.week-chip--selected {
    background: #0D47A1;
    border-color: #0D47A1;
}
.week-star {
    font-size: 8px !important;
    position: absolute;
    top: 3px;
    right: 4px;
    color: inherit;
    opacity: 0.8;
}
.data-dot {
    position: absolute;
    top: 4px;
    right: 4px;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: #4CAF50;
}

/* Footer */
.dialog-footer {
    border-top: 1px solid #eeeeee;
    background: #fafafa;
}
.selected-counter {
    display: flex;
    align-items: center;
}
.counter-text {
    font-size: 13px;
}
</style>
